import __i18nConfig from '@next-translate-root/i18n.js';
import __loadNamespaces from 'next-translate/loadNamespaces';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { UnavailableForLegalReasonsPage } from '@/components/Pages/Errors/UnavailableForLegalReasonsPage';
import { InsightViewerPage } from '@/components/Pages/Insights/InsightViewerPage';
import { getBaseAnalytics } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import { BaseAppContextContainer } from '@/contexts/AppContext/AppContextContainer';
import { getAppContextServerSideProps } from '@/contexts/AppContext/AppContextServer';
import type { IAttachment, IContentItem } from '@/interfaces';
import { ContentType } from '@/interfaces';
import type { IBaseAnalyticsData } from '@/services/Analytics/bnefAnalyticsInterfaces';
import { TrackedProductTypes } from '@/services/Analytics/bnefAnalyticsSettings';
import { getLoginRedirectUrl } from '@/utils/siteUtils';
import type { ComponentProps } from 'react';
import { getPublicServerSideProps } from '../[id]';
const getInitialAnalyticsMetadata = (insight: IContentItem): IBaseAnalyticsData => ({
  page: {
    pageInfo: {
      pageName: 'BNEF: Insights: Content View'
    },
    category: {
      siteSection: 'BNEF: Insights'
    },
    content: {
      type: TrackedProductTypes.Insight,
      id: `${TrackedProductTypes.Insight}-${insight.id}`
    }
  }
});
const getServerSideProps = getAppContextServerSideProps(async ({
  query,
  res,
  req,
  api
}) => {
  try {
    const insightId = query.id as string;
    const continueReading = query.continue === 'true';
    const result = await api.get({
      path: '/insights/[ID]',
      query: {
        language: query?.language && query.language.toString()
      },
      replacements: {
        id: insightId
      }
    });
    if (result?.insight?.attachments?.some((x: IAttachment) => x.type === 'pdf')) {
      api.post({
        path: '/users/content_view',
        body: {
          contentId: insightId,
          contentType: ContentType.insight
        }
      }).catch(() => {});
      const lang = query?.language ? `?language=${query.language.toString()}` : '';
      return {
        props: {
          analyticsMetadata: getInitialAnalyticsMetadata(result.insight),
          pdfPath: `/downloads/insights/${insightId}/pdf${lang}`,
          watermark: result.watermark,
          insight: result.insight,
          attachmentsWithAllowance: result.attachmentsWithAllowance,
          reportLocales: result.reportLocales,
          continueReading,
          readingProgress: result.readingProgress,
          userAnnotations: result.userAnnotations
        }
      };
    }
  } catch (e) {
    if (e?.response?.status === 403) {
      const location = getLoginRedirectUrl(req.url);
      return {
        redirect: {
          destination: location,
          statusCode: 302
        }
      };
    }
    if (e?.response?.status === 451) {
      res.statusCode = e.response.status;
      return {
        props: {
          analyticsMetadata: getBaseAnalytics(ContentType.insight),
          responseCode: e.response.status
        }
      };
    }
    throw e;
  }
  return {
    notFound: true
  };
}, getPublicServerSideProps, true);
export default class extends BaseAppContextContainer<ComponentProps<typeof InsightViewerPage> & {
  responseCode?: number;
}> {
  public render() {
    const {
      insight
    } = this.props;
    if (this.props?.responseCode === 451) {
      return <UnavailableForLegalReasonsPage />;
    }
    return <>
                <HeadMeta canonicalPathOverride={`/insights/${insight.id}`} title={`${insight.title} | Full Report`} type={ContentType.insight} image={insight.leadImage?.baseUrl} publishDate={insight.displayDate} description={insight.summary} tags={insight.tags} />
                <InsightViewerPage {...this.props} />
            </>;
  }
}
async function __Next_Translate__getServerSideProps__1a1a756c7c5__(ctx) {
  if (typeof global !== 'undefined') {
    global.i18nConfig = __i18nConfig;
  }
  const res = await getServerSideProps(ctx);
  return {
    ...res,
    props: {
      ...(res.props || {}),
      ...(await __loadNamespaces({
        ...ctx,
        ...__i18nConfig,
        pathname: '/insights/[id]/view',
        loaderName: 'getServerSideProps',
        loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({}))
      }))
    }
  };
}
export { __Next_Translate__getServerSideProps__1a1a756c7c5__ as getServerSideProps };